import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@munifocus/ui';
import {
  signatureProblem,
  type DocumentSignoffView,
  type SignatureMethod,
  type SignoffDecision,
} from '@munifocus/contracts-gov';
import { useDecideSignoff, useMySignature } from '../../lib/signoff-api';
import { useMutationToast } from '../../lib/mutation-toast';
import { SignaturePad } from './SignaturePad';

/**
 * Decide one sign-off request: approve and sign, request changes, or reject.
 *
 * Approving asks for the reviewer's password again — signing is the one act in
 * the portal that puts a person's name on a legal instrument, so an unattended
 * session must not be able to do it — and for a signature, drawn here or taken
 * from the reviewer's profile.
 */
export function DecisionDialog({
  signoff,
  open,
  onClose,
}: {
  signoff: DocumentSignoffView;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useMutationToast();
  const decide = useDecideSignoff();
  const stored = useMySignature(open);

  const [decision, setDecision] = useState<SignoffDecision>('approve');
  const [comment, setComment] = useState('');
  const [method, setMethod] = useState<SignatureMethod>('drawn');
  const [drawn, setDrawn] = useState<string | null>(null);
  const [saveSignature, setSaveSignature] = useState(false);
  const [password, setPassword] = useState('');

  const hasStored = Boolean(stored.data?.hasSignature);
  const approving = decision === 'approve';
  const ready = approving
    ? signatureProblem({ method, imageDataUrl: drawn ?? undefined, password }) === null
    : comment.trim().length > 0;

  const submit = () => {
    decide.mutate(
      {
        fileId: signoff.fileId,
        id: signoff.id,
        dto: approving
          ? {
              decision,
              method,
              ...(method === 'drawn' ? { imageDataUrl: drawn ?? undefined, saveSignature } : {}),
              password,
              ...(comment.trim() ? { comment: comment.trim() } : {}),
            }
          : { decision, comment: comment.trim() },
      },
      {
        onSuccess: () => {
          toast.success(`gov.signoff.decided.${decision}`);
          setPassword('');
          onClose();
        },
        onError: (err) => toast.error(err, 'gov.signoff.decideFailed'),
      },
    );
  };

  const kind = t(`gov.forms.kind.${signoff.formKind}`, { defaultValue: signoff.formKind });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.signoff.dialog.title', { document: kind })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t('gov.signoff.dialog.summary', {
              file: signoff.fileReference,
              version: signoff.formVersion,
              name: signoff.submittedByName,
            })}
          </Typography>
          {signoff.submitNote && <Alert severity="info">{signoff.submitNote}</Alert>}
          {signoff.documentUrl && (
            <Link href={signoff.documentUrl} target="_blank" rel="noopener" underline="hover">
              {t('gov.signoff.dialog.openDocument')}
            </Link>
          )}

          <ToggleButtonGroup
            exclusive
            size="small"
            value={decision}
            onChange={(_e, value: SignoffDecision | null) => value && setDecision(value)}
            aria-label={t('gov.signoff.dialog.decision')}
          >
            <ToggleButton value="approve">{t('gov.signoff.decision.approve')}</ToggleButton>
            <ToggleButton value="request_changes">
              {t('gov.signoff.decision.request_changes')}
            </ToggleButton>
            <ToggleButton value="reject">{t('gov.signoff.decision.reject')}</ToggleButton>
          </ToggleButtonGroup>

          {approving && (
            <Stack spacing={1.5}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={method}
                onChange={(_e, value: SignatureMethod | null) => value && setMethod(value)}
                aria-label={t('gov.signoff.sign.method')}
              >
                <ToggleButton value="drawn">{t('gov.signoff.sign.drawn')}</ToggleButton>
                <ToggleButton value="stored" disabled={!hasStored}>
                  {t('gov.signoff.sign.stored')}
                </ToggleButton>
              </ToggleButtonGroup>

              {method === 'drawn' && (
                <>
                  <SignaturePad onChange={setDrawn} />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={saveSignature}
                        onChange={(e) => setSaveSignature(e.target.checked)}
                      />
                    }
                    label={t('gov.signoff.sign.saveToProfile')}
                  />
                </>
              )}
              {method === 'stored' && stored.data?.imageUrl && (
                <Box
                  component="img"
                  src={stored.data.imageUrl}
                  alt={t('gov.signoff.sign.storedAlt')}
                  sx={{ maxHeight: 80, maxWidth: 320, objectFit: 'contain' }}
                />
              )}

              <TextField
                type="password"
                label={t('gov.signoff.sign.password')}
                helperText={t('gov.signoff.sign.passwordHint')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                fullWidth
              />
            </Stack>
          )}

          <TextField
            label={
              approving ? t('gov.signoff.dialog.commentOptional') : t('gov.signoff.dialog.comment')
            }
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            required={!approving}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button variant="contained" disabled={!ready || decide.isPending} onClick={submit}>
          {approving ? t('gov.signoff.dialog.sign') : t(`gov.signoff.decision.${decision}`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
